import { useEffect, useMemo } from 'react';
import { type QueryClient, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { schoolApi } from '../../../api/school.api';
import type { Instructor } from '../../../types';

// Photo de profil d'un moniteur. L'API en renvoie l'adresse (`photoUrl`, `null` sans photo) dans
// `/v1/team` et `/v1/lessons/instructors`. La route est protégée (jeton en en-tête) : une balise
// `<img src>` ne peut pas la lire directement, l'image est donc chargée ici puis affichée depuis une
// adresse locale. Sans photo, pendant le chargement ou en cas d'échec, l'écran affiche les initiales.

const PHOTO_KEY = 'team-photo';

/** Charge une photo et vérifie qu'elle s'affiche ; renvoie son adresse locale. */
async function loadPhoto(photoUrl: string): Promise<string> {
  const local = URL.createObjectURL(await schoolApi.fetchTeamPhoto(photoUrl));
  try {
    const probe = new Image();
    probe.src = local;
    await probe.decode();
    return local;
  } catch (error) {
    URL.revokeObjectURL(local);
    throw error;
  }
}

// L'adresse porte la version de la photo : une photo remplacée change d'adresse, une adresse donnée
// ne change jamais de contenu. Une image déjà en mémoire est donc réutilisée telle quelle, même
// quand l'écran recharge toutes ses données.
const photoQuery = (photoUrl: string) => ({
  queryKey: [PHOTO_KEY, photoUrl],
  queryFn: ({ client }: { client: QueryClient }) => client.getQueryData<string>([PHOTO_KEY, photoUrl]) ?? loadPhoto(photoUrl),
  staleTime: Infinity,
  retry: false,
});

/** Libère l'adresse locale d'une photo quand elle sort de la mémoire (page quittée, école changée). */
const watched = new WeakSet<QueryClient>();
function useReleasedPhotos() {
  const client = useQueryClient();
  useEffect(() => {
    if (watched.has(client)) return;
    watched.add(client);
    client.getQueryCache().subscribe((event) => {
      const local = event.query.state.data;
      if (event.type === 'removed' && event.query.queryKey[0] === PHOTO_KEY && typeof local === 'string') URL.revokeObjectURL(local);
    });
  }, [client]);
}

/** Image d'une photo de profil prête à afficher, ou `undefined` (initiales). */
export function useTeamPhoto(photoUrl?: string | null): string | undefined {
  useReleasedPhotos();
  return useQuery({ ...photoQuery(photoUrl ?? ''), enabled: !!photoUrl }).data;
}

/**
 * Photos des moniteurs du planning : renvoie `instructorPhoto(id)`, l'image prête à afficher du
 * moniteur ou `undefined` (initiales). Un compte moniteur sans fiche d'équipe n'a pas de photo.
 */
export function useInstructorPhotos(instructors: Pick<Instructor, 'id' | 'photoUrl'>[]) {
  useReleasedPhotos();
  const withPhoto = instructors.filter((inst) => inst.photoUrl);
  const images = useQueries({ queries: withPhoto.map((inst) => photoQuery(inst.photoUrl as string)) });
  // Ne change que lorsqu'une image arrive ou qu'une photo est remplacée (les écrans mémorisent leurs colonnes).
  const loaded = JSON.stringify(withPhoto.map((inst, i) => [inst.id, images[i]?.data ?? '']));
  return useMemo(() => {
    const byInstructor = new Map<string, string>(JSON.parse(loaded));
    return (instructorId?: string | null): string | undefined => (instructorId && byInstructor.get(instructorId)) || undefined;
  }, [loaded]);
}
